import Image from 'next/image';
import { Button } from '../../components/general/button';
import { Meta } from '../../components/meta/meta';

// images
import { InferGetStaticPropsType } from 'next';
import furnitureGif from '../../assets/home/furniture.gif';
import hero from '../../assets/home/home-hero-min.jpg';
import { BlueArch, DeliverStepImg, DonateStepImg, DreamStepImg, JumpingKid } from '../../components/img/svgs';

import { getStaticProps, HomeHeroBed, Since2011 } from '../home';
export { getStaticProps };

export default function ImpactReportHome({ delivered }: InferGetStaticPropsType<typeof getStaticProps>) {
  return (
    <main className="relative">
      <Meta />
      {/* Header */}
      <section className="to-fold-home relative w-full overflow-hidden">
        <Image
          alt="Beds for Kids Warehouse Loading Dock"
          src={hero}
          className="absolute z-0 h-full w-full object-cover"
          priority
        />
        <div className="absolute bottom-0 left-0 right-0 top-0 bg-[#0a0a19] opacity-[65%]" />
        <div className="relative mx-auto mt-[var(--nav-height)] max-w-3xl pt-[5vh] lg:pt-[40vh]">
          <h1 className="mx-4 text-center font-operetta text-xl font-bold text-white xxs:text-3xl lg:text-4xl">
            The Beds for Kids <br />
            2023 Impact Report is Live!
          </h1>
          <div className="mt-[40vh] flex w-full justify-center xs:mt-[45vh] lg:mt-[15vh] xl:mt-[10vh]">
            <Button size="xl" bType="secondary" link="/where-donations-go/#impact-report">
              <span className="flex items-center">
                <span>See How We&#39;re Making a Difference</span>
                <span className="ml-2">&#8594;</span>
              </span>
            </Button>
          </div>
        </div>
        <HomeHeroBed />
      </section>
      {/* Fold (white 1) */}
      <section className="relative bg-white pt-8 text-blue-500 lg:pt-4 xl:pt-0">
        <div className="mx-auto mb-16 flex max-w-4xl flex-col gap-4 px-4 text-center">
          <h2 className="relative text-center font-operetta text-4xl lg:text-7xl">
            furnishing futures<sup className="-top-3 text-lg lg:-top-7 lg:text-2xl">TM</sup>
          </h2>
          <p className="font-avenir text-lg leading-6">
            We strive to serve thousands of children and their families each year. Though the number beds and furniture
            delivered increases with each year, so does the number of people we are still having to turn down. Without
            the support of our community, we would not be able to continue advancing our mission of providing beds and
            essential furniture to children and their families in need. Together we are furnishing futures!
          </p>
          <div>
            <Button link="/how-to-help" className="px-12 font-avenir !text-base">
              how to help
            </Button>
          </div>
        </div>
        <div className="lg:px-auto mx-auto grid max-w-4xl gap-8 px-4 lg:grid-cols-2">
          <Image src={furnitureGif} alt="furniture gif" className="max-w-full" />
          <div className="flex flex-col justify-center gap-4 text-center lg:text-left">
            <h3 className="font-operetta text-6xl">
              not <span className="font-caliban text-7xl font-normal text-green-500">just</span> beds
            </h3>
            <p className="font-avenir text-lg leading-6">
              We&#39;re not JUST beds for kids, we are a furniture bank. In addition to beds, donate your new or gently
              used furniture items and lamps to help us fully furnish a family&#39;s home.
            </p>
            <div className="text-center lg:text-left">
              <Button link="/about" className="px-12 font-avenir !text-base">
                about us
              </Button>
            </div>
          </div>
        </div>
        <BlueArch className="mt-16" />
      </section>
      {/* Blue 1 */}
      <Since2011 delivered={delivered} />
      {/* White 2 */}
      <div className="mt-1 text-center">
        <Button bType="secondary" link="/donate">
          donate now
        </Button>
      </div>
      <section className="relative bg-white pt-16 text-blue-500">
        <h2 className="relative mx-auto max-w-2xl px-4 text-center font-operetta text-4xl lg:text-6xl">our process</h2>
        <div className="mx-auto mt-8 grid max-w-5xl gap-x-16 gap-y-4 px-16 lg:grid-cols-3 lg:px-0">
          <div className="text-center">
            <DonateStepImg />
            <p className="font-avenir text-5xl">
              you <span className="font-caliban text-6xl text-green-500">donate</span>
            </p>
          </div>
          <div className="text-center">
            <DeliverStepImg />
            <p className="font-avenir text-5xl">
              we <span className="font-caliban text-6xl text-green-500">deliver</span>
            </p>
          </div>
          <div className="text-center">
            <DreamStepImg />
            <p className="font-avenir text-5xl">
              they <span className="font-caliban text-6xl text-green-500">dream</span>
            </p>
          </div>
        </div>
        <p className="mx-auto mt-8 max-w-3xl px-8 text-center font-avenir text-lg leading-6">
          Beds for Kids relies on generous donations of furniture items from the community throughout the year. Once
          those items are received they are brought back to our warehouse for cleaning and repair. This allows us to
          recycle furniture and creates a sustainable way of providing for the needs of our community. The final step in
          the process is delivery day. The Beds for Kids team along with the support of volunteers delivers these items
          to families in need in the Charlotte region.
        </p>
        <div className="mt-8 text-center">
          <Button link="/who-we-are/">learn more</Button>
        </div>
        <BlueArch className="mt-16" />
      </section>
      {/* Blue 2 (to bottom) */}
      <section className="bg-blue-500 pb-16 text-white">
        <div className="mx-auto grid max-w-5xl lg:grid-cols-3">
          <div className="relative overflow-x-clip lg:overflow-x-visible">
            <JumpingKid className="mx-[25%] -mt-6 lg:absolute lg:-right-24 lg:-top-12 lg:mx-auto lg:mt-0 lg:px-8" />
          </div>
          <div className="flex flex-col px-8 py-4 text-center lg:col-span-2 lg:px-0 lg:pt-16 lg:text-left">
            <h2 className="font-operetta text-5xl">our families</h2>
            <p className="font-avenir text-xl">
              We work with Referring Agencies all over the Charlotte Area to help get matched with families in need.
            </p>
            <div className="mt-4">
              <Button bType="secondary-white" link="/agencies">
                become a referring agency
              </Button>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
